import React, { useEffect, useState } from 'react';
import { CheckIcon, GripVerticalIcon, PlusIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { createStage, setInitialStage, updateOrganization, updateStage } from '../../services/organizationService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { FONT_OPTIONS } from '../../data/fonts';
import { Organization } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
export function Settings() {
  const {
    organization
  } = useAuth();
  const {
    stages
  } = useWorkspace();
  const [draft, setDraft] = useState<Organization | null>(organization);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newStage, setNewStage] = useState('');
  useEffect(() => {
    if (organization && (!draft || draft.id !== organization.id)) setDraft(organization);
  }, [organization, draft]);
  if (!draft) return null;
  function update(partial: Partial<Organization>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  async function handleSave() {
    setSaving(true);
    await updateOrganization(draft!.id, draft!);
    setSaving(false);
    setSaved(true);
  }
  return <>
      <PageHeader title="Configuración" description="Datos, identidad visual y preferencias de tu empresa." actions={<>
            {saved && <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1347" data-mp="2udt2xd8zy0pv">
                <CheckIcon className="h-4 w-4" data-id="element-1348" data-mp="xd6vewvptiyf" /> Guardado
              </span>}
            <Button onClick={handleSave} loading={saving} data-id="element-1349" data-mp="12ct5ixg8yj30">
              Guardar cambios
            </Button>
          </>} data-id="element-1346" data-mp="2yg7o44051ckb" />

      <Tabs defaultValue="empresa" data-id="element-1350" data-mp="1752ti7xsx92n">
        <TabsList data-id="element-1351" data-mp="1764fh7gcvfz5">
          <TabsTrigger value="empresa" data-id="element-1352" data-mp="1c34krg5dsep7">Empresa</TabsTrigger>
          <TabsTrigger value="identidad" data-id="element-1353" data-mp="1c2l5y8b126fe">Branding</TabsTrigger>
          <TabsTrigger value="pipeline" data-id="element-1354" data-mp="eg5m4zv4owoo">Embudo de ventas</TabsTrigger>
          <TabsTrigger value="notificaciones" data-id="element-1355" data-mp="jegx5dx5td8h">Notificaciones</TabsTrigger>
          <TabsTrigger value="privacidad" data-id="element-1356" data-mp="jep9n4pdegj5">Privacidad</TabsTrigger>
        </TabsList>

        <TabsContent value="empresa" data-id="element-1357" data-mp="obywqdpaeg3t">
          <div className="grid gap-4 rounded-lg border border-border bg-background p-5 sm:grid-cols-2" data-id="element-1358" data-mp="tcmvxmno6394">
            <Input id="o-name" label="Nombre de la empresa" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1359" data-mp="1zsp4i19czfzj" />
            <Select id="o-sector" label="Sector" options={SECTOR_OPTIONS} value={draft.sector} onChange={e => update({
            sector: e.target.value
          })} data-id="element-1360" data-mp="24q8jj3dvrwsj" />
            <Input id="o-email" label="Email de contacto" type="email" value={draft.email ?? ''} onChange={e => update({
            email: e.target.value
          })} data-id="element-1361" data-mp="3kahj89y1fli8" />
            <Input id="o-phone" label="Teléfono" value={draft.phone ?? ''} onChange={e => update({
            phone: e.target.value
          })} data-id="element-1362" data-mp="3akxzl22i3nmb" />
            <Input id="o-web" label="Web" value={draft.website ?? ''} onChange={e => update({
            website: e.target.value
          })} data-id="element-1363" data-mp="35mmpqrc7hd43" />
            <Input id="o-address" label="Dirección" value={draft.address ?? ''} onChange={e => update({
            address: e.target.value
          })} data-id="element-1364" data-mp="2wl5epndno1m9" />
            <Select id="o-tz" label="Zona horaria" options={[{
            value: 'Europe/Madrid',
            label: 'Europe/Madrid'
          }, {
            value: 'Atlantic/Canary',
            label: 'Atlantic/Canary'
          }, {
            value: 'America/Mexico_City',
            label: 'America/Mexico_City'
          }, {
            value: 'America/Bogota',
            label: 'America/Bogota'
          }, {
            value: 'America/Argentina/Buenos_Aires',
            label: 'Buenos Aires'
          }]} value={draft.timezone} onChange={e => update({
            timezone: e.target.value
          })} data-id="element-1365" data-mp="2rndjokhprzdu" />
            <Input id="o-logo" label="Logo (URL)" value={draft.logoUrl ?? ''} onChange={e => update({
            logoUrl: e.target.value
          })} data-id="element-1366" data-mp="3epoxmbuo2dld" />
          </div>
        </TabsContent>

        <TabsContent value="identidad" data-id="element-1367" data-mp="34qpq4wln0nzy">
          <div className="space-y-6 rounded-2xl border border-border bg-background p-6 sm:p-8" data-id="element-1368" data-mp="2qyjjc14djtd6">
            <p className="text-sm text-muted-foreground" data-id="element-1369" data-mp="2vwuq22zdhnr6">
              Este logotipo y estos colores se aplican automáticamente en el
              dashboard, el menú lateral, tus landings, tus formularios, el pie de
              página, la página de gracias y los emails.
            </p>

            <LogoUploader organizationId={draft.id} kind="logo" value={draft.brand.logoUrl ?? draft.logoUrl} onChange={value => update({
            logoUrl: value,
            brand: {
              ...draft.brand,
              logoUrl: value
            }
          })} data-id="element-1370" data-mp="22cvasflg3ij9" />

            <Input id="b-name" label="Nombre de la empresa" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1371" data-mp="2c985ngsq82fl" />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1372" data-mp="2laxtydrbal95">
              <Input id="b-primary" label="Color principal" type="color" value={draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                primaryColor: e.target.value
              }
            })} data-id="element-1373" data-mp="2hanmewnvfms6" />
              <Input id="b-secondary" label="Color secundario" type="color" value={draft.brand.secondaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryColor: e.target.value
              }
            })} data-id="element-1374" data-mp="2q4sm85kzg8ju" />
              <Input id="b-button" label="Color de botones" type="color" value={draft.brand.buttonColor ?? draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                buttonColor: e.target.value
              }
            })} data-id="element-1375" data-mp="375em3r9pqoyn" />
              <Input id="b-text" label="Color de textos" type="color" value={draft.brand.textColor ?? '#0B0B0F'} onChange={e => update({
              brand: {
                ...draft.brand,
                textColor: e.target.value
              }
            })} data-id="element-1376" data-mp="3lu1bymwyuz44" />
              <Input id="b-bg" label="Color de fondo" type="color" value={draft.brand.backgroundColor ?? '#FFFFFF'} onChange={e => update({
              brand: {
                ...draft.brand,
                backgroundColor: e.target.value
              }
            })} data-id="element-1377" data-mp="3nmp1ygpzetl" />
              <Input id="b-favicon" label="Favicon (URL)" value={draft.brand.faviconUrl ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                faviconUrl: e.target.value
              }
            })} data-id="element-1378" data-mp="25h2rrtq30k4x" />
              <Select id="b-font" label="Tipografía principal" options={FONT_OPTIONS} value={draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                fontFamily: e.target.value
              }
            })} data-id="element-1379" data-mp="1y2k215cir69g" />
              <Select id="b-font2" label="Tipografía secundaria" options={FONT_OPTIONS} value={draft.brand.secondaryFontFamily ?? draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryFontFamily: e.target.value
              }
            })} data-id="element-1380" data-mp="2lsw6g68k2kva" />
              <Input id="b-domain" label="Dominio propio" placeholder="paginas.miempresa.com" value={draft.brand.domain ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                domain: e.target.value
              }
            })} data-id="element-1381" data-mp="26wonf98vvphl" />
            </div>

            <div className="rounded-xl border border-border bg-surface p-5" data-id="element-1382" data-mp="1l4m86rhu0yu9">
              <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1383" data-mp="1q3p51onnryr5">
                Vista previa
              </p>
              <div className="flex flex-wrap items-center gap-4 rounded-lg px-5 py-4" style={{
              backgroundColor: draft.brand.backgroundColor ?? '#FFFFFF',
              fontFamily: `${draft.brand.fontFamily}, Inter, sans-serif`
            }} data-id="element-1384" data-mp="1c5s4kh633877">
                <BrandLogo src={draft.brand.logoUrl ?? draft.logoUrl} size={24} data-id="element-1385" data-mp="28uzstwy37vet" />
                <span className="text-sm font-semibold" style={{
                color: draft.brand.secondaryColor
              }} data-id="element-1386" data-mp="1v31odzfbmnoz">
                  {draft.name}
                </span>
                <span className="text-sm" style={{
                color: draft.brand.textColor ?? '#5F6673',
                fontFamily: `${draft.brand.secondaryFontFamily ?? draft.brand.fontFamily}, Inter, sans-serif`
              }} data-id="element-1387" data-mp="1z2ai54ad2lkj">
                  Texto de ejemplo
                </span>
                <span className="ml-auto inline-flex h-9 items-center rounded-lg px-4 text-sm font-medium text-white" style={{
                backgroundColor: draft.brand.buttonColor ?? draft.brand.primaryColor
              }} data-id="element-1388" data-mp="g3mcyaldu11y">
                  Botón principal
                </span>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" data-id="element-1389" data-mp="3tdaatm7ujfex">
          <div className="rounded-lg border border-border bg-background p-5" data-id="element-1390" data-mp="3tebxyp24zsd0">
            <ul className="space-y-2" data-id="element-1391" data-mp="3of8sx4m5pm51">
              {stages.map(stage => <li key={stage.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3" data-id="element-1392" data-mp="75kfu64rrsjy">
                  <GripVerticalIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-1393" data-mp="25emmliupxzr" />
                  <Input id={`s-name-${stage.id}`} value={stage.name} onChange={e => updateStage(stage.id, {
                name: e.target.value
              })} data-id="element-1394" data-mp="1ij4p5nuboe7a" />
                  <input type="color" aria-label={`Color de ${stage.name}`} value={stage.color} onChange={e => updateStage(stage.id, {
                color: e.target.value
              })} className="h-9 w-12 cursor-pointer rounded-md border border-border bg-background" data-id="element-1395" data-mp="jyq4g4ubnpzg" />
                  <Switch id={`s-active-${stage.id}`} label="Activo" checked={stage.active} onChange={checked => updateStage(stage.id, {
                active: checked
              })} data-id="element-1396" data-mp="a62adhuq804f" />
                  <Button size="sm" variant={stage.isInitial ? 'primary' : 'ghost'} onClick={() => setInitialStage(stage.organizationId, stage.id)} data-id="element-1397" data-mp="84mivgs45cc">
                    {stage.isInitial ? 'Estado inicial' : 'Marcar como inicial'}
                  </Button>
                </li>)}
            </ul>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row" data-id="element-1398" data-mp="3ihygbzgjpsd7">
              <Input id="new-stage" placeholder="Nombre del nuevo estado" value={newStage} onChange={e => setNewStage(e.target.value)} data-id="element-1399" data-mp="1lm91rt4sqjo8" />
              <Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1401" data-mp="1o7zz3n3znn12" />} disabled={!newStage.trim()} onClick={async () => {
              await createStage(draft.id, newStage.trim(), '#3D8BFF');
              setNewStage('');
            }} data-id="element-1400" data-mp="1gmedzygbia7e">
                Añadir estado
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notificaciones" data-id="element-1402" data-mp="22x5x57r3b77d">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1403" data-mp="22w4o18tmwusa">
            {([['newLead', 'Nuevo lead'], ['leadAssigned', 'Lead asignado'], ['statusChange', 'Cambio de estado'], ['newMeeting', 'Nueva reunión']] as const).map(([key, label]) => <Switch key={key} id={`n-${key}`} label={label} checked={draft.notifications[key]} onChange={checked => update({
            notifications: {
              ...draft.notifications,
              [key]: checked
            }
          })} data-id="element-1404" data-mp="1f723nt7m8nu3" />)}
          </div>
        </TabsContent>

        <TabsContent value="privacidad" data-id="element-1405" data-mp="2vl20gvb5zxd4">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1406" data-mp="2vjqnc4vtxd9c">
            <Input id="p-privacy" label="URL de la política de privacidad" value={draft.privacy.privacyPolicyUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              privacyPolicyUrl: e.target.value
            }
          })} data-id="element-1407" data-mp="vhr4471rjlx5" />
            <Input id="p-legal" label="URL del aviso legal" value={draft.privacy.legalNoticeUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              legalNoticeUrl: e.target.value
            }
          })} data-id="element-1408" data-mp="10i5dptj0j3xa" />
            <Textarea id="p-consent" label="Texto de consentimiento" rows={3} value={draft.privacy.consentText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              consentText: e.target.value
            }
          })} data-id="element-1409" data-mp="7rn224483u89" />
            <Textarea id="p-cookies" label="Texto de cookies" rows={3} value={draft.privacy.cookiesText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              cookiesText: e.target.value
            }
          })} data-id="element-1410" data-mp="3p96adpwcm6nm" />
          </div>
        </TabsContent>
      </Tabs>
    </>;
}